import z from "zod";

import { item, withMeaning, type AnswerMeaning } from "../../authoring.js";
import { need, strength } from "./responseSets.js";

/**
 * A test instrument cut down from the eCANS 6+ assessment, so the screens and
 * rules built on meanings meet real CANS wording at its real length: three
 * domains always asked and a module opened by a rating on one of their items.
 *
 * Every word is the eCANS wording as it was copied, typos included ("ratesthe",
 * the repeated "Developmental"), so never correct one here: a correction is a
 * new revision. It is test content, so it stays in the `sample_cans` family and
 * binds nothing a real CANS family publishes later.
 */

export const name = ["sample_cans", "mini", "1.0", "r1"] as const;

export const groups = {
  strengths: "strengths",
  life_functioning: "life_functioning",
  behavioural_emotional_needs: "behavioural_emotional_needs",
  developmental_needs: "developmental_needs",
} as const;

export const items = {
  family_strengths: "family_strengths",
  interpersonal: "interpersonal",
  optimism: "optimism",

  family_functioning: "family_functioning",
  living_situation: "living_situation",
  developmental_intellectual: "developmental_intellectual",

  psychosis: "psychosis",
  impulsivity_hyperactivity: "impulsivity_hyperactivity",
  attention_concentration: "attention_concentration",

  cognitive: "cognitive",
  developmental: "developmental",
  communication: "communication",
} as const;

/** Every key an earlier version asked and this one does not. The first version retires nothing. */
export const retiredItems = [] as const;

// The strength scale offers the same values as the needs scale.
type Rating = z.infer<typeof need>;

type FourRatings = readonly [string, string, string, string];

const ratings = [0, 1, 2, 3] as const satisfies readonly Rating[];

/**
 * An item's ratings on a scale: each titled by the scale's own words for it,
 * and described by the item's words for it.
 */
const ratedOn =
  (scale: FourRatings) =>
  (descriptors: FourRatings): readonly AnswerMeaning<Rating>[] =>
    ratings.map((value) => ({
      value,
      title: { en: scale[value] },
      description: { en: descriptors[value] },
    }));

const strengthRatings = ratedOn([
  "Well-developed, centrepiece strength; may be used as a focus of an intervention/ plan.",
  "Identified and useful strength. Strength will be used, maintained or built upon in plan.",
  "Strengths have been identified but require strength-building efforts before they can be effectively utilised as part of a plan. Identified but not useful.",
  "An area in which no current strength is identified; efforts may be recommended to develop a strength in this area.",
]);

const needRatings = ratedOn([
  "No evidence of any needs; no need for action.",
  "Identified need that requires monitoring, watchful waiting, or preventive action based on history, suspicion or disagreement",
  "Action is required to ensure that the identified need is addressed; need is interfering with functioning.",
  "Need is dangerous or disabling; requires immediate and/or intensive action.",
]);

const strengths = withMeaning(
  z
    .object({
      ...item(items.family_strengths, strength, {
        title: { en: "Family Strengths" },
        description: {
          en: "This item refers to the presence of a sense of family identity as well as love and communication among family members. Even families who are struggling often have a firm foundation that consists of a positive sense of family and strong underlying love and commitment to each other. These are the constructs this strength is intended to identify. As with Family Functioning, the definition of family comes from the child's perspective (i.e., who the child describes as their family). If this information is not known, then we recommend a definition of family that includes biological/adoptive relatives and their significant others with whom the child is still in contact.",
        },
        answers: strengthRatings([
          "Family has strong relationships and significant family strengths. This level indicates a family with much love and respect for one another. There is at least one family member who has a strong loving relationship with the child and is able to provide significant emotional or concrete support. Child is fully included in family activities.",
          "Family has some good relationships and good communication. Family members are able to enjoy each other's company. There is at least one family member who has a strong, loving relationship with the child and is able to provide limited emotional or concrete support.",
          "Family needs some assistance in developing relationships and/or communications. Family members are known, but currently none are able to provide emotional or concrete support",
          "Family needs significant assistance in developing relationships and communications, or child has no identified family. Child is not included in normal family activities.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.interpersonal, strength, {
        title: { en: "Interpersonal" },
        description: {
          en: "This item is used to identify a child's social and relationship skills. Interpersonal skills are rated independently of Social Functioning because a child can have social skills but still struggle in their relationships at a particular point in time. This strength indicates an ability to make and maintain longstanding relationships.",
        },
        answers: strengthRatings([
          "Significant interpersonal strengths. Child/young person has well-developed interpersonal skills and healthy friendships.",
          "Child/young person has good interpersonal skills and has shown the ability to develop healthy friendships.",
          "Child/young person requires strength building to learn to develop good interpersonal skills and/or healthy friendships. Child/young person has some social skills that facilitate positive relationships with peers and adults but may not have any current healthy friendships.",
          "There is no evidence of observable interpersonal skills or healthy friendships at this time and/or child/young person requires significant help to learn to develop interpersonal skills and healthy friendships.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.optimism, strength, {
        title: { en: "Optimism" },
        description: {
          en: "This item should be rated based on the child/young person's sense of self in their own future. This ratesthe child/young person's future orientation.",
        },
        answers: strengthRatings([
          "Child/young person has a strong and stable optimistic outlook for their future.",
          "Child/young person is generally optimistic about their future.",
          "Child/young person has difficulty maintaining a positive view of themselves and their life. Child/young person's outlook may vary from overly optimistic to overly pessimistic.",
          "There is no evidence of optimism at this time and/or child/young person has difficulties seeing positive aspects about themselves or their future.",
        ]),
      })
        .withComment()
        .build(),
    })
    .strict(),
  {
    title: { en: "Child Strengths Domain (6+)" },
    description: {
      en: "This domain describes the assets of the child/young person that can be used to advance healthy development. It is important to remember that strengths are NOT the opposite of needs. Increasing a child/young person's strengths while also addressing his or her behavioural/emotional needs leads to better functioning, and better outcomes, than does focusing just on the individual's needs. Identifying areas where strengths can be built is a significant element of service planning. In these items the 'best' assets and resources available to the child/young person are rated based on how accessible and useful those strengths are. These are the only items that use the Strength Rating Scale with action levels.",
    },
  },
);

const lifeFunctioning = withMeaning(
  z
    .object({
      ...item(items.family_functioning, need, {
        title: { en: "Family Functioning" },
        description: {
          en: "This rates the child/young person's relationships with those who are in the child/young person's family. It is recommended that the description of family should come from the child/young person's perspective (i.e. who the child/young person describes as their family). In the absence of this information, consider biological and adoptive relatives and their significant others with whom the child/young person is still in contact. Foster families should only be considered if they have made a significant commitment to the child/young person. For child/young person involved with child welfare, family refers to the person(s) fulfilling the permanency plan. When rating this item, take into account the relationship the child/young person has with the child/young person's family as well as the relationship of the family as a whole.",
        },
        answers: needRatings([
          "No evidence of problems in relationships with family members, and/or child/young person is doing well in relationships with family members.",
          "History or suspicion of problems. Child/young person might be doing adequately in relationships with family members, although some problems may exist. For example, some family members may have problems in their relationships with child/young person. Arguing may be common but does not result in major problems.",
          "Child/young person is having problems with parents, siblings and/or other family members that are impacting the child/young person's functioning. Frequent arguing, difficulty maintaining positive relationships may be observed.",
          "Child/young person is having severe problems with parents, siblings, and/or other family members. This would include problems of domestic violence, absence of any positive relationships, etc.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.living_situation, need, {
        title: { en: "Living Situation" },
        description: {
          en: "This item refers to how the child/young person is functioning in the child/young person's current living arrangement, which could be with a relative, in a foster home, etc. This item should exclude respite, brief detention/jail, and brief medical and psychiatric hospitalisation.",
        },
        answers: needRatings([
          "No evidence of problem with functioning in current living environment. Child/young person and caregivers feel comfortable dealing with issues that come up in day-to-day life.",
          "Child/young person experiences mild problems with functioning in current living situation. Caregivers express some concern about child/young person's behaviour in living situation, and/or child/young person and caregiver have some difficulty dealing with issues that arise in daily life.",
          "Child/young person has moderate to severe problems with functioning in current living situation. Child/young person's difficulties in maintaining appropriate behaviour in this setting are creating significant problems for others in the residence. Child/young person and caregivers have difficulty interacting effectively with each other much of the time.",
          "Child/young person has profound problems with functioning in current living situation. Child/young person is at immediate risk of being removed from living situation due to problematic behaviours.",
        ]),
      })
        .withComment()
        .build(),
      // The asterisk is part of the eCANS title, marking an item whose rating
      // can open a module.
      ...item(items.developmental_intellectual, need, {
        title: { en: "Developmental/Intellectual*" },
        description: {
          en: "This item describes the child's development as compared to standard developmental milestones, as well as rates the presence of any developmental (motor, social and speech) or intellectual disabilities. It includes Intellectual Developmental Disorder (IDD) and Autism Spectrum Disorders. Rate the item depending on the significance of the disability and the related level of impairment in personal, social, family, school, or occupational functioning.",
        },
        answers: needRatings([
          "No evidence of developmental delay and/or child has no developmental problems or intellectual disability.",
          "There are concerns about possible developmental delay. Child may have low IQ, a documented delay, or documented borderline intellectual disability (i.e. FSIQ 70-85). Mild deficits in adaptive functioning are indicated.",
          "Child has mild developmental delays (e.g., deficits in social functioning, inflexibility of behaviour causing functional problems in one or more settings) and/or mild to moderate Intellectual Disability/Intellectual Disability Disorder. (If available, FSIQ 55-69.) IDD impacts communication, social functioning, daily living skills, judgment, and/or risk of manipulation by others.",
          "Child has severe to profound intellectual disability (FSIQ, if available, less than 55) and/or Autism Spectrum Disorder with marked to profound deficits in adaptive functioning in one or more areas: communication, social participation and independent living across multiple environments.",
        ]),
      })
        .withComment()
        .build(),
    })
    .strict(),
  {
    title: { en: "Life Functioning Domain (6+)" },
    description: {
      en: "Life domains are the different arenas of social interaction found in the lives of children, young people, and their families. This domain rates how they are functioning in the individual, family, peer, school, and community realms. This section is rated using the needs scale and therefore will highlight any struggles the individual and family are experiencing.",
    },
  },
);

const behaviouralEmotionalNeeds = withMeaning(
  z
    .object({
      ...item(items.psychosis, need, {
        title: { en: "Psychosis (Thought Disorder)" },
        description: {
          en: "This item rates the symptoms of psychiatric disorders with a known neurological base, including schizophrenia spectrum and other psychotic disorders. The common symptoms of these disorders include hallucinations (i.e. experiencing things others do not experience), delusions (i.e. a false belief or an incorrect inference about reality that is firmly sustained despite the fact that nearly everybody thinks the belief is false or proof exists of its inaccuracy), disorganised thinking, and bizarre/idiosyncratic behaviour.",
        },
        answers: needRatings([
          "No evidence of psychotic symptoms. Both thought processes and content are within normal range.",
          "Evidence of disruption in thought processes or content. Child may be somewhat tangential in speech or evidence somewhat illogical thinking (age-inappropriate). This also includes a child with a history of hallucinations but none currently. Use this category for children who are below the threshold for one of the diagnoses listed above.",
          "Evidence of disturbance in thought process or content that may be impairing the child's functioning in at least one life domain. Child may be somewhat delusional or have brief intermittent hallucinations. Speech may be at times quite tangential or illogical.",
          "Clear evidence of dangerous hallucinations, delusions, or bizarre behaviour that might be associated with some form of psychotic disorder that places the child or others at risk of physical harm.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.impulsivity_hyperactivity, need, {
        title: { en: "Impulsivity/Hyperactivity" },
        description: {
          en: "Problems with impulse control and impulsive behaviours, including motoric disruptions, are rated here. This includes behavioural symptoms associated with Attention-Deficit Hyperactivity Disorder (ADHD) and Impulse-Control Disorders as indicated in clinical criteria. Children with impulse problems tend to engage in behaviour without thinking, regardless of the consequences. This can include compulsions to engage in gambling, violent behaviour (e.g., road rage), sexual behaviour, fire-starting or stealing.",
        },
        answers: needRatings([
          "No evidence of symptoms of loss of control of behaviour.",
          "There is a history or evidence of mild levels of impulsivity evident in action or thought that place the child at risk of future functioning difficulties. The child may exhibit limited impulse control, e.g., child may yell out answers to questions or may have difficulty waiting one's turn. Some motor difficulties may be present as well, such as pushing or shoving others.",
          "Clear evidence of problems with impulsive, distractible, or hyperactive behaviour that interferes with the child's functioning in at least one life domain. This indicates a child with impulsive behaviour who may represent a significant management problem for adults (e.g., caregivers, teachers, coaches, etc.). A child who often intrudes on others and often exhibits aggressive impulses would be rated here.",
          "Clear evidence of a dangerous level of hyperactivity and/or impulsive behaviour that places the child at risk of physical harm. This indicates a child with frequent and significant levels of impulsive behaviour that carries considerable safety risk (e.g., running into the street, dangerous driving or bike riding). The child may be impulsive on a nearly continuous basis. The child endangers self or others without thinking.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.attention_concentration, need, {
        title: { en: "Attention/Concentration" },
        description: {
          en: "Problems with attention, concentration and task completion would be rated here. These may include symptoms that are part of Attention-Deficit Hyperactivity Disorder. Inattention/distractibility not related to opposition would also be rated here.",
        },
        answers: needRatings([
          "This rating is used to indicate a child with no evidence of attention or concentration problems. This child is able to stay on task in an age-appropriate manner.",
          "This rating is used to indicate a child with evidence of mild problems with attention or concentration. Child may have some difficulties staying on task for an age-appropriate time period in school or play.",
          "This rating is used to indicate a child with moderate attention problems. In addition to problems with sustained attention, child may become easily distracted or forgetful in daily activities, have trouble following through on activities, and become reluctant to engage in activities that require sustained effort. A child who meets diagnostic criteria for ADHD would be rated here.",
          "This rating is used to indicate a child with severe impairment of attention or concentration. A child with profound symptoms of ADHD or significant attention difficulties related to another diagnosis would be rated here.",
        ]),
      })
        .withComment()
        .build(),
    })
    .strict(),
  { title: { en: "Behavioural/Emotional Needs Domain (6+)" } },
);

const developmentalNeeds = withMeaning(
  z
    .object({
      ...item(items.cognitive, need, {
        title: { en: "Cognitive" },
        description: {
          en: "This item rates the child/young person's IQ and cognitive functioning.",
        },
        answers: needRatings([
          "Child/young person's intellectual functioning appears to be in normal range. There is no reason to believe that the child/young person has any problems with intellectual functioning.",
          "Child/young person has mild learning disability or a suspected problem with intellectual functioning that requires investigation. IQ if available is between 70 - 85.",
          "Child/young person has mild learning disability. IQ if available is between 55 and 70.",
          "Child/young person has moderate to profound learning disability. IQ if available is less than 55.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.developmental, need, {
        title: { en: "Developmental" },
        description: {
          en: "Developmental This item rates the level of developmental delay/disorders that are present.",
        },
        answers: needRatings([
          "Child/young person's development appears within normal range. There is no reason to believe that the child/young person has any developmental problems.",
          "Evidence of a mild developmental delay.",
          "Evidence of a pervasive developmental disorder including Autism, Tourette's, Down's Syndrome or other significant developmental delay.",
          "Severe developmental disorder.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.communication, need, {
        title: { en: "Communication" },
        description: {
          en: "This item rates the child/young person's ability to communicate with others via expression and reception.",
        },
        answers: needRatings([
          "Child/young person's receptive and expressive communication appears developmentally appropriate. There is no reason to believe that the child/young person has any problems communicating.",
          "Child/young person has receptive communication skills but limited expressive communication skills.",
          "Child/young person has both limited receptive and expressive communication skills.",
          "Child/young person is unable to communicate.",
        ]),
      })
        .withComment()
        .build(),
    })
    .strict(),
  { title: { en: "Developmental Needs Module" } },
);

export const schema = z
  .object({
    [groups.strengths]: strengths,
    [groups.life_functioning]: lifeFunctioning,
    [groups.behavioural_emotional_needs]: behaviouralEmotionalNeeds,
    // A module, rated only where developmental or intellectual needs call for it.
    [groups.developmental_needs]: developmentalNeeds.optional(),
  })
  .strict();
